/*
 * Copyright (c) All right reserved Anvish Inc. 2025
 * This project and any portion thereof may not be reproduced or used in any manner without the express written permission of Anvish Inc.
 */

import React, { createContext, useContext, useEffect, useState } from "react";

import { CartItem, pruneEmptyCategories, withoutItem } from "@/app/cartUtils";

export { countCartItems } from "@/app/cartUtils";

interface CartContextType {
  cart: Record<string, Record<string, CartItem>>;
  addToCart: (
    category: string,
    materialCode: string,
    addons: string[],
    qty: number,
    wheelAlignmentAddons?: string,
  ) => void;
  removeFromCart: (category: string, materialCode: string) => void;
  updateQty: (category: string, materialCode: string, qty: number) => void;
  updateAddons: (
    category: string,
    materialCode: string,
    addons: string[],
    wheelAlignmentAddons?: string,
  ) => void;
  clearCart: () => void;
  isCartInitialized: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [cart, setCart] = useState<Record<string, Record<string, CartItem>>>(
    {},
  );
  const [isCartInitialized, setIsCartInitialized] = useState(false);

  // ✅ Load cart from localStorage on first render
  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedCart = localStorage.getItem("cart");

      if (storedCart) {
        try {
          // Also repairs carts saved before the empty-category fix.
          setCart(pruneEmptyCategories(JSON.parse(storedCart) || {}));
        } catch {
          console.error("Failed to parse stored cart data.");
        }
      }
      setIsCartInitialized(true);
    }
  }, []);

  // ✅ Save cart to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== "undefined" && isCartInitialized) {
      localStorage.setItem("cart", JSON.stringify(cart));
    }
  }, [cart]);

  const addToCart = (
    category: string,
    materialCode: string,
    addons: string[],
    qty: number,
    wheelAlignmentAddons?: string,
  ) => {
    setCart((prevCart) => ({
      ...prevCart,
      [category]: {
        ...(prevCart[category] || {}),
        [materialCode]: {
          material_code: materialCode,
          addons: addons,
          qty: qty,
          wheelAlignmentAddons: wheelAlignmentAddons || "",
        },
      },
    }));
  };

  const removeFromCart = (category: string, materialCode: string) => {
    setCart((prevCart) => {
      if (!prevCart[category]?.[materialCode]) return prevCart;

      return withoutItem(prevCart, category, materialCode);
    });
  };

  const updateQty = (category: string, materialCode: string, qty: number) => {
    setCart((prevCart) => {
      const item = prevCart[category]?.[materialCode];

      if (!item) return prevCart;

      if (qty <= 0) {
        // Removing the last item of a category must also remove the category,
        // otherwise the cart is left as { category: {} } and never reads empty.
        return withoutItem(prevCart, category, materialCode);
      }

      return {
        ...prevCart,
        [category]: { ...prevCart[category], [materialCode]: { ...item, qty } },
      };
    });
  };

  const updateAddons = (
    category: string,
    materialCode: string,
    addons: string[],
    wheelAlignmentAddons?: string,
  ) => {
    setCart((prevCart) => {
      const item = prevCart[category]?.[materialCode];

      if (!item) return prevCart;

      return {
        ...prevCart,
        [category]: {
          ...prevCart[category],
          [materialCode]: {
            ...item,
            addons,
            wheelAlignmentAddons: wheelAlignmentAddons || "",
          },
        },
      };
    });
  };

  const clearCart = () => {
    setCart({});
    localStorage.removeItem("cart");
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQty,
        updateAddons,
        clearCart,
        isCartInitialized,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }

  return context;
};
